import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { agent } from "../../../../utils/agent";
import * as API from "../../../../utils/apiPath";
import { userLoginData } from "../../../../actions/userActions";

// Async Thunks

export const fetchUniversityPartners = createAsyncThunk(
  "register/fetchUniversityPartners",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_UNIVERSITY);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchIndustries = createAsyncThunk(
  "register/fetchIndustries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_INDUSTRY);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProgramStudies = createAsyncThunk(
  "register/fetchProgramStudies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.PROGRAM_STUDY_ID);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRegisterSettings = createAsyncThunk(
  "register/fetchRegisterSettings",
  async (input, { rejectWithValue }) => {
    try {
      const roleId = typeof input === "object" ? input?.roleId : input;
      const stepKey = typeof input === "object" ? input?.stepKey : "step_1";
      const payload = {
        role_type: localStorage?.getItem("role_id") || roleId,
        step_key: stepKey || "step_1",
      };
      const response = await agent.get(API.REGISTER_SETTING, {
        params: payload,
      });
      if (response?.data?.status === 200) {
        return response.data.data;
      }
      return rejectWithValue("Failed to fetch settings");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAreas = createAsyncThunk(
  "register/fetchAreas",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_AREAS_LIST, {});
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchObjectives = createAsyncThunk(
  "register/fetchObjectives",
  async (roleID, { rejectWithValue }) => {
    try {
      const response = await agent.get(
        API.LOAD_ORGANIZATION_LIST_SIGNUP + `/${roleID}/objective`,
        {}
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchParticularPrograms = createAsyncThunk(
  "register/fetchParticularPrograms",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.GET_PAETICULAR_PROGRAM);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLanguages = createAsyncThunk(
  "register/fetchLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_LANGUAGE);
      const formattedData = response.data.data?.map((item) => {
        return { value: item?.title, label: item?.title };
      });
      return formattedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyEmail = createAsyncThunk(
  "register/verifyEmail",
  async (validatingString, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.VERIFY_EMAIL, {
        params: { validating_string: validatingString },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchExperience = createAsyncThunk(
  "register/fetchExperience",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.EXPERIENCE);
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Board-related async thunks
export const fetchCoursesCompleted = createAsyncThunk(
  "register/fetchCoursesCompleted",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.GET_COURSES_COMPLETED);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBoardExperienceMentor = createAsyncThunk(
  "register/fetchBoardExperienceMentor",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.GET_BOARD_EXPERIENCE_MENTOR, {
        params: { role_id: roleId }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBoardEntityTypeExperience = createAsyncThunk(
  "register/fetchBoardEntityTypeExperience",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.GET_BOARD_ENTITY_TYPE_EXPERIENCE, {
        params: { role_id: roleId }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAccreditedDirector = createAsyncThunk(
  "register/fetchAccreditedDirector",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.GET_ACCREDITED_DIRECTOR, {
        params: { role_id: roleId }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBoardExperienceLevel = createAsyncThunk(
  "register/fetchBoardExperienceLevel",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await agent.get(`${API.GET_BOARD_EXPERIENCE_LEVEL}?role_id=${roleId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBoardExperience = createAsyncThunk(
  "register/fetchBoardExperience",
  async (role_id, { rejectWithValue }) => {
    try {
      const response = await agent.get(`${API.GET_BOARD_EXPERIENCE}?role_id=${role_id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserData = createAsyncThunk(
  "register/fetchUserData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await agent.get(`${API.GETUSERDATA}/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Helper function to determine redirect path
const getRedirectPath = (step, terminateProcess) => {
  if (terminateProcess === "Yes") return "/";
  
  const stepRoutes = {
    step_1: "/registration-step-1",
    step_2: "/registration-step-2",
    step_3: "/registration-step-3",
    step_4: "/registration-step-4",
    step_5: "/"
  };
  
  return stepRoutes[step] || "/";
};

// Helper function to handle step 5 logic
const handleStep5Completion = async (terminateProcess, paramsLogin, dispatch) => {
  if (terminateProcess === "No" && paramsLogin) {
    await dispatch(userLoginData(paramsLogin)); // ✅ AUTO LOGIN
  }
  localStorage.removeItem("userId");
};

// Helper function to validate response
const isValidResponse = (response) => {
  return response.status === 200 && response?.data?.status === 200;
};

// Replicating userRegisterData logic
export const registerUser = createAsyncThunk(
  "register/registerUser",
  async (
    { params, userId, terminateProcess, step, paramsLogin },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await agent.post(API.REGISTER_NEW, params, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      localStorage.setItem("userId", userId);

      if (response.status === 503) {
        return rejectWithValue({
            success: false,
            message: response.data.message
        });
      }

      if (!isValidResponse(response)) {
        return rejectWithValue({
            success: false,
            message: response.data.message
        });
      }

      const redirectTo = getRedirectPath(step, terminateProcess);
      
      if (step === "step_5") {
        await handleStep5Completion(terminateProcess, paramsLogin, dispatch);
      }

      return {
        success: true,
        message: response.data.message,
        redirectTo,
        data: response.data
      };
      
    } catch (error) {
      return rejectWithValue({
          success: false,
          message: error.message
      });
    }
  }
);

const initialState = {
  universityPartners: [],
  industries: [],
  programStudies: [],
  registerStepFields: [],
  areas: [],
  objectives: [],
  particularPrograms: [],
  languages: [],
  experienceData: [],
  userDataResponse: null,
  verifyEmailData: null,
  
  // Board-related data
  coursesCompleted: [],
  boardExperienceMentor: [],
  boardEntityTypeExperience: [],
  accreditedDirector: [],
  boardExperienceLevel: [],
  boardExperience: [],
  
  loading: false,
  error: null,
  
  // Registration status
  registrationStatus: "idle",
  registrationMessage: "",
  registrationRedirectPath: null,
};

const registerSlice = createSlice({
  name: "register",
  initialState,
  reducers: {
    resetRegistrationStatus: (state) => {
      state.registrationStatus = "idle";
      state.registrationMessage = "";
      state.registrationRedirectPath = null;
    },
    resetVerifyEmailData: (state) => {
        state.verifyEmailData = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUniversityPartners
      .addCase(fetchUniversityPartners.fulfilled, (state, action) => {
        state.universityPartners = action.payload || [];
      })
      // fetchIndustries
      .addCase(fetchIndustries.fulfilled, (state, action) => {
        state.industries = action.payload || [];
      })
      // fetchProgramStudies
      .addCase(fetchProgramStudies.fulfilled, (state, action) => {
        state.programStudies = action.payload || [];
      })
      // fetchRegisterSettings
      .addCase(fetchRegisterSettings.fulfilled, (state, action) => {
        state.registerStepFields = action.payload || [];
      })
      // fetchAreas
      .addCase(fetchAreas.fulfilled, (state, action) => {
        state.areas = action.payload || [];
      })
      // fetchObjectives
      .addCase(fetchObjectives.fulfilled, (state, action) => {
        state.objectives = action.payload || [];
      })
      // fetchParticularPrograms
      .addCase(fetchParticularPrograms.fulfilled, (state, action) => {
        state.particularPrograms = action.payload || [];
      })
      // fetchLanguages
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.languages = action.payload || [];
      })
      // fetchExperience
      .addCase(fetchExperience.fulfilled, (state, action) => {
        state.experienceData = action.payload || [];
      })
      // Board-related reducers
      .addCase(fetchCoursesCompleted.fulfilled, (state, action) => {
        state.coursesCompleted = action.payload || [];
      })
      .addCase(fetchBoardExperienceMentor.fulfilled, (state, action) => {
        state.boardExperienceMentor = action.payload || [];
      })
      .addCase(fetchBoardEntityTypeExperience.fulfilled, (state, action) => {
        state.boardEntityTypeExperience = action.payload || [];
      })
      .addCase(fetchAccreditedDirector.fulfilled, (state, action) => {
        state.accreditedDirector = action.payload || [];
      })
      .addCase(fetchBoardExperienceLevel.fulfilled, (state, action) => {
        state.boardExperienceLevel = action.payload || [];
      })
      .addCase(fetchBoardExperience.fulfilled, (state, action) => {
        state.boardExperience = action.payload || [];
      })
      // verifyEmail
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.verifyEmailData = action.payload;
      })
      // fetchUserData
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.userDataResponse = action.payload;
      })
      // registerUser
      .addCase(registerUser.pending, (state) => {
        state.registrationStatus = "loading";
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.registrationStatus = "succeeded";
        state.registrationMessage = action.payload.message;
        state.registrationRedirectPath = action.payload.redirectTo;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.registrationStatus = "failed";
        state.error = action.payload?.message || action.payload;
        state.registrationMessage = action.payload?.message || "Registration failed";
      });
  },
});

export const { resetRegistrationStatus, resetVerifyEmailData } = registerSlice.actions;

export default registerSlice.reducer;
